import { useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, X } from '@phosphor-icons/react';

type Option<T extends number | string> = {
  value: T;
  label: string;
  // Segunda línea opcional (gris, menor jerarquía). Cuando está presente, la
  // opción se renderiza en dos filas. El filtro de búsqueda también la incluye.
  sublabel?: string;
};

type Props<T extends number | string> = {
  options: Option<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  // Si true, muestra un botón X dentro del trigger para limpiar el valor.
  clearable?: boolean;
  // Si false, oculta el input de búsqueda (útil cuando hay pocas opciones).
  searchable?: boolean;
};

export function SearchableSelect<T extends number | string>({
  options,
  value,
  onChange,
  placeholder = '— Seleccionar —',
  emptyText = 'Sin resultados',
  disabled = false,
  className = '',
  clearable = true,
  searchable = true,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = value !== null && value !== undefined
    ? options.find(o => o.value === value) ?? null
    : null;

  // Filtrado case-insensitive sobre el label. Sin buscador, no se filtra.
  const filtered = useMemo(() => {
    if (!searchable) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o =>
      `${o.label} ${o.sublabel ?? ''}`.toLowerCase().includes(q));
  }, [query, options, searchable]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  // Cuando se abre, autofocus del input para empezar a tipear directo.
  useEffect(() => {
    if (open && searchable) inputRef.current?.focus();
  }, [open, searchable]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed text-left"
      >
        <span className={selected ? 'text-stone-900 truncate' : 'text-stone-400'}>
          {selected ? (
            <>
              {selected.label}
              {selected.sublabel && (
                <span className="text-stone-400 ml-1.5">{selected.sublabel}</span>
              )}
            </>
          ) : placeholder}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {clearable && selected && !disabled && (
            <span
              role="button"
              aria-label="Limpiar"
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
              className="text-stone-400 hover:text-stone-700 p-0.5 rounded hover:bg-stone-200"
            >
              <X size={12} weight="bold" />
            </span>
          )}
          <CaretDown size={14} weight="bold" className="text-stone-500" />
        </span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded shadow-lg border border-stone-200 bg-white">
          {searchable && (
            <div className="p-2 border-b border-stone-100">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar…"
                className="w-full px-2 py-1.5 text-sm border border-stone-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-green-100"
              />
            </div>
          )}
          <ul className="max-h-60 overflow-auto py-1" role="listbox">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-stone-400">{emptyText}</li>
            )}
            {filtered.map(opt => {
              const isSelected = opt.value === value;
              return (
                <li
                  key={String(opt.value)}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => { onChange(opt.value); setOpen(false); setQuery(''); }}
                  className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-stone-100 ${
                    isSelected ? 'bg-stone-200 font-medium' : ''
                  }`}
                >
                  {opt.sublabel ? (
                    <div className="flex flex-col leading-tight">
                      <span className="text-stone-900">{opt.label}</span>
                      <span className="text-xs text-stone-400">{opt.sublabel}</span>
                    </div>
                  ) : (
                    opt.label
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
